import { useState, useEffect, useRef, useCallback } from 'react';
import { Package, Warning, ArrowUp, ArrowDown, Plus, X, Barcode } from '@phosphor-icons/react';
import api from '../api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const INP = {
  background: 'hsl(0,0%,8%)', border: '1px solid hsl(0,0%,16%)',
  padding: '8px 10px', color: 'hsl(0,0%,85%)', fontFamily: "'JetBrains Mono', monospace",
  fontSize: 11, outline: 'none', width: '100%', boxSizing: 'border-box',
};
const LBL = {
  display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
  color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4,
};

function ItemTypeBadge({ type }) {
  const map = { RM: 'chip-info', WIP: 'chip-warning', FG: 'chip-active' };
  return <span className={map[type] || 'chip-neutral'}>{type}</span>;
}

function AddItemModal({ onClose, onSaved }) {
  const [uoms, setUoms] = useState([]);
  const [form, setForm] = useState({
    item_code: '', item_name: '', item_type: 'RM', unit: 'pcs',
    qty_available: '', qty_reserved: '', qty_qc_hold: '',
    reorder_level: '', location: '', batch_no: '', batch_tracked: false,
  });
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const animRef = useRef(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const stopCamera = useCallback(() => {
    if (animRef.current) { cancelAnimationFrame(animRef.current); animRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setScanning(false);
  }, []);

  const startScan = useCallback(async () => {
    setScanError('');
    if (!('BarcodeDetector' in window)) {
      setScanError('Barcode scanning not supported in this browser. Please use Chrome or Edge.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      setScanning(true);
      // Wait for video element to be rendered
      setTimeout(() => {
        if (!videoRef.current) return;
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        const detector = new window.BarcodeDetector({ formats: ['code_128', 'code_39', 'ean_13', 'ean_8', 'qr_code', 'upc_a', 'upc_e'] });
        const scan = async () => {
          if (!videoRef.current || videoRef.current.readyState < 2) {
            animRef.current = requestAnimationFrame(scan); return;
          }
          try {
            const codes = await detector.detect(videoRef.current);
            if (codes.length > 0) {
              set('item_code', codes[0].rawValue);
              stopCamera();
              return;
            }
          } catch (_) {}
          animRef.current = requestAnimationFrame(scan);
        };
        animRef.current = requestAnimationFrame(scan);
      }, 200);
    } catch (err) {
      setScanError('Camera access denied or unavailable.');
      setScanning(false);
    }
  }, [stopCamera]);

  // Cleanup on unmount
  useEffect(() => () => stopCamera(), [stopCamera]);

  useEffect(() => {
    api.get('/masters/uoms').then(r => setUoms(r.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/inventory/items', {
        ...form,
        qty_available: parseFloat(form.qty_available) || 0,
        qty_reserved: parseFloat(form.qty_reserved) || 0,
        qty_qc_hold: parseFloat(form.qty_qc_hold) || 0,
        reorder_level: parseFloat(form.reorder_level) || 0,
      });
      onSaved();
    } catch (err) {
      console.error(err.response?.data || err);
    }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,20%)',
        width: '100%', maxWidth: 560, padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 15,
            color: 'hsl(0,0%,95%)', textTransform: 'uppercase' }}>Add Stock Item</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none',
            cursor: 'pointer', color: 'hsl(0,0%,50%)' }}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          {scanning && (
            <div style={{ marginBottom: 12, position: 'relative', background: 'hsl(0,0%,0%)',
              border: '1px solid hsl(197,100%,48%)', overflow: 'hidden' }}>
              <video ref={videoRef} style={{ width: '100%', display: 'block', maxHeight: 220, objectFit: 'cover' }}
                playsInline muted />
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: '60%', height: 2, background: 'hsl(197,100%,48%)', opacity: 0.7 }} />
              </div>
              <button type="button" onClick={stopCamera}
                style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.7)',
                  border: '1px solid hsl(0,0%,30%)', color: 'hsl(0,0%,80%)', cursor: 'pointer',
                  padding: '4px 10px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }}>
                Cancel
              </button>
              <div style={{ padding: '4px 8px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                color: 'hsl(197,100%,48%)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Point camera at barcode…
              </div>
            </div>
          )}
          {scanError && (
            <div style={{ marginBottom: 8, padding: '6px 10px', background: 'hsl(0,60%,10%)',
              border: '1px solid hsl(0,60%,30%)', color: 'hsl(0,80%,65%)',
              fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }}>
              {scanError}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={LBL}>Item Code *</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input required value={form.item_code} onChange={e => set('item_code', e.target.value)}
                  data-testid="new-item-code" style={{ ...INP, flex: 1 }} placeholder="RM-INK-BL-02" />
                <button type="button" onClick={startScan} title="Scan barcode"
                  data-testid="scan-barcode-btn"
                  style={{ background: 'hsl(0,0%,12%)', border: '1px solid hsl(0,0%,22%)',
                    color: 'hsl(197,100%,48%)', cursor: 'pointer', padding: '0 10px',
                    display: 'flex', alignItems: 'center' }}>
                  <Barcode size={16} />
                </button>
              </div>
            </div>
            <div>
              <label style={LBL}>Item Type</label>
              <select value={form.item_type} onChange={e => set('item_type', e.target.value)}
                data-testid="new-item-type" style={{ ...INP, appearance: 'auto' }}>
                <option value="RM">RM — Raw Material</option>
                <option value="WIP">WIP — Work In Progress</option>
                <option value="FG">FG — Finished Goods</option>
              </select>
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={LBL}>Item Name *</label>
              <input required value={form.item_name} onChange={e => set('item_name', e.target.value)}
                data-testid="new-item-name" style={INP} placeholder="Blue Ink Formulation" />
            </div>
            <div>
              <label style={LBL}>Unit</label>
              <select value={form.unit} onChange={e => set('unit', e.target.value)}
                data-testid="new-item-unit" style={{ ...INP, appearance: 'auto' }}>
                {uoms.length > 0
                  ? uoms.map(u => <option key={u.id} value={u.code}>{u.name} ({u.code})</option>)
                  : ['pcs','kg','ml','g','box','l','m'].map(u => <option key={u}>{u}</option>)
                }
              </select>
            </div>
            <div>
              <label style={LBL}>Location</label>
              <input value={form.location} onChange={e => set('location', e.target.value)}
                style={INP} placeholder="Store-A, Bin-R5" />
            </div>
            <div>
              <label style={LBL}>Opening Qty</label>
              <input type="number" step="any" value={form.qty_available}
                onChange={e => set('qty_available', e.target.value)} style={INP} placeholder="0" />
            </div>
            <div>
              <label style={LBL}>Reorder Level</label>
              <input type="number" step="any" value={form.reorder_level}
                onChange={e => set('reorder_level', e.target.value)} style={INP} placeholder="0" />
            </div>
            <div>
              <label style={LBL}>Batch No</label>
              <input value={form.batch_no} onChange={e => set('batch_no', e.target.value)}
                style={INP} placeholder="Optional" />
            </div>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: 12 }}>
                <input
                  type="checkbox"
                  data-testid="inventory-batch-tracked-checkbox"
                  checked={form.batch_tracked || false}
                  onChange={e => set('batch_tracked', e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,85%)' }}>
                  Batch Tracked
                </span>
              </label>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="save-new-item-btn">
              {saving ? 'Saving...' : 'Add Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [adjustItem, setAdjustItem] = useState(null);
  const [adjustQty, setAdjustQty] = useState('');
  const [showAddItem, setShowAddItem] = useState(false);

  const loadItems = () => {
    api.get('/inventory').then(r => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { loadItems(); }, []);

  const handleAdjust = async () => {
    if (!adjustItem || !adjustQty) return;
    try {
      await api.put(`/inventory/${adjustItem.id}/adjust`, { qty_change: parseFloat(adjustQty) });
      loadItems();
      setAdjustItem(null);
      setAdjustQty('');
    } catch (e) { console.error(e); }
  };

  const isLowStock = (item) => item.qty_available <= item.reorder_level;
  const isOnHold = (item) => item.qty_qc_hold > 0;

  const filtered = filter === 'all' ? items :
    filter === 'low' ? items.filter(isLowStock) :
    items.filter(i => i.item_type === filter);

  const stats = {
    rm: items.filter(i => i.item_type === 'RM').length,
    fg: items.filter(i => i.item_type === 'FG').length,
    lowStock: items.filter(isLowStock).length,
    onHold: items.filter(isOnHold).length,
  };

  const chartData = items.filter(i => i.item_type === 'RM').slice(0, 8).map(i => ({
    name: i.item_name.split(' ')[0],
    available: i.qty_available,
    reserved: i.qty_reserved,
    reorder: i.reorder_level,
  }));

  return (
    <div data-testid="inventory-page">
      {/* Header */}
      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 22, fontWeight: 700,
            color: 'hsl(0,0%,98%)', textTransform: 'uppercase', marginBottom: 4 }}>Inventory</h1>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,45%)',
            textTransform: 'uppercase', letterSpacing: '0.12em' }}>
            Stock Control Center
          </div>
        </div>
        <button data-testid="add-stock-item-btn" onClick={() => setShowAddItem(true)} className="btn-primary"
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={14} weight="bold" /> Add Item
        </button>
      </div>

      {/* Stats */}
      <div className="metrics-row" style={{ marginBottom: 16 }}>
        {[
          { label: 'Raw Materials', val: stats.rm, color: 'hsl(200,80%,55%)', icon: Package },
          { label: 'Finished Goods', val: stats.fg, color: 'hsl(197,100%,48%)', icon: Package },
          { label: 'Low Stock', val: stats.lowStock, color: 'hsl(0,84%,60%)', icon: Warning },
          { label: 'On QC Hold', val: stats.onHold, color: 'hsl(12,90%,55%)', icon: Warning },
        ].map(s => (
          <div key={s.label} data-testid={`inv-stat-${s.label.toLowerCase().replace(/ /g, '-')}`}
            style={{ flex: 1, background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,14%)', padding: '14px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,45%)',
                textTransform: 'uppercase', letterSpacing: '0.12em' }}>{s.label}</div>
              <s.icon size={14} color={s.color} />
            </div>
            <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 26, fontWeight: 700,
              color: s.color }}>{s.val}</div>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,14%)', padding: 20, marginBottom: 16 }}>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(0,0%,50%)',
          textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 16 }}>
          Raw Material Stock Levels
        </div>
        <ResponsiveContainer width="100%" height={140}>
          <BarChart data={chartData}>
            <XAxis dataKey="name" tick={{ fill: 'hsl(0,0%,45%)', fontFamily: "'JetBrains Mono', monospace", fontSize: 9 }}
              axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: 'hsl(0,0%,40%)', fontFamily: "'JetBrains Mono', monospace", fontSize: 9 }}
              axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ background: 'hsl(0,0%,8%)', border: '1px solid hsl(0,0%,18%)',
              fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }} />
            <Bar dataKey="available" fill="hsl(197,100%,48%)" name="Available" />
            <Bar dataKey="reserved" fill="hsl(12,90%,55%)" name="Reserved" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {['all', 'RM', 'FG', 'low'].map(f => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`inv-filter-${f}`}
            style={{ padding: '5px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
              textTransform: 'uppercase', letterSpacing: '0.08em', cursor: 'pointer', border: '1px solid',
              borderColor: filter === f ? 'hsl(197,100%,48%)' : 'hsl(0,0%,18%)',
              background: filter === f ? 'hsl(197,100%,48%)' : 'transparent',
              color: filter === f ? '#000' : 'hsl(0,0%,55%)', transition: 'all 0.15s' }}>
            {f === 'low' ? 'Low Stock' : f === 'all' ? 'All Items' : f}
          </button>
        ))}
      </div>

      {/* Stock table */}
      <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,14%)' }}>
        <div className="table-scroll">
        <table className="erp-table" data-testid="inventory-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Item Code</th>
              <th>Item Name</th>
              <th>Batch</th>
              <th>Available</th>
              <th>Reserved</th>
              <th>QC Hold</th>
              <th>Unit</th>
              <th>Location</th>
              <th>Reorder Level</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={12} style={{ padding: 20, color: 'hsl(0,0%,40%)' }}>Loading...</td></tr>
            ) : filtered.map(item => (
              <tr key={item.id} data-testid={`inv-row-${item.item_code}`}
                style={{ borderLeft: isLowStock(item) ? '3px solid hsl(0,84%,60%)' : '3px solid transparent' }}>
                <td><ItemTypeBadge type={item.item_type} /></td>
                <td style={{ color: 'hsl(197,100%,48%)', fontWeight: 600, fontSize: 11 }}>{item.item_code}</td>
                <td style={{ color: 'hsl(0,0%,85%)' }}>{item.item_name}</td>
                <td style={{ color: 'hsl(0,0%,50%)', fontSize: 10 }}>{item.batch_no || '—'}</td>
                <td style={{ color: isLowStock(item) ? 'hsl(0,84%,60%)' : 'hsl(197,100%,48%)', fontWeight: 700 }}>
                  {item.qty_available?.toLocaleString()}
                </td>
                <td style={{ color: 'hsl(12,90%,55%)' }}>{item.qty_reserved?.toLocaleString()}</td>
                <td style={{ color: item.qty_qc_hold > 0 ? 'hsl(12,90%,55%)' : 'hsl(0,0%,40%)' }}>
                  {item.qty_qc_hold?.toLocaleString()}
                </td>
                <td style={{ color: 'hsl(0,0%,55%)' }}>{item.unit}</td>
                <td style={{ color: 'hsl(0,0%,55%)', fontSize: 10 }}>{item.location}</td>
                <td style={{ color: 'hsl(0,0%,50%)' }}>{item.reorder_level?.toLocaleString()}</td>
                <td>
                  {isLowStock(item) ? (
                    <span className="chip-danger">LOW STOCK</span>
                  ) : item.qty_qc_hold > 0 ? (
                    <span className="chip-hold">QC HOLD</span>
                  ) : (
                    <span className="chip-active">OK</span>
                  )}
                </td>
                <td>
                  <button data-testid={`adjust-btn-${item.item_code}`}
                    onClick={() => setAdjustItem(item)}
                    style={{ padding: '3px 8px', background: 'transparent', border: '1px solid hsl(0,0%,20%)',
                      color: 'hsl(0,0%,55%)', fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                      cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.08em',
                      transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.target.style.borderColor = 'hsl(197,100%,48%)'; e.target.style.color = 'hsl(197,100%,48%)'; }}
                    onMouseLeave={e => { e.target.style.borderColor = 'hsl(0,0%,20%)'; e.target.style.color = 'hsl(0,0%,55%)'; }}>
                    ADJUST
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {showAddItem && (
        <AddItemModal onClose={() => setShowAddItem(false)}
          onSaved={() => { setShowAddItem(false); loadItems(); }} />
      )}

      {/* Adjust Modal */}
      {adjustItem && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 100,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,18%)', width: 380, padding: 28 }}>
            <h3 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 16, textTransform: 'uppercase',
              color: 'hsl(0,0%,95%)', marginBottom: 16 }}>Stock Adjustment</h3>
            <div style={{ marginBottom: 16, fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
              color: 'hsl(0,0%,70%)' }}>{adjustItem.item_name}</div>
            <div style={{ marginBottom: 16, fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
              color: 'hsl(0,0%,50%)' }}>
              Current: <span style={{ color: 'hsl(197,100%,48%)' }}>{adjustItem.qty_available} {adjustItem.unit}</span>
            </div>
            <label style={{ display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
              color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 6 }}>
              Adjustment Qty (+ or -)
            </label>
            <input value={adjustQty} onChange={e => setAdjustQty(e.target.value)} data-testid="adjust-qty-input"
              type="number" placeholder="e.g. +50 or -20"
              style={{ width: '100%', background: 'hsl(0,0%,8%)', border: '1px solid hsl(0,0%,16%)',
                padding: '10px', color: 'hsl(0,0%,85%)', fontFamily: "'JetBrains Mono', monospace",
                fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 20 }} />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => { setAdjustItem(null); setAdjustQty(''); }} className="btn-ghost">Cancel</button>
              <button onClick={handleAdjust} data-testid="confirm-adjust-btn" className="btn-primary">CONFIRM</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
